import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { AlertCircleIcon } from 'lucide-react-native';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Keyboard, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { z } from 'zod';

import { getCancelHeaderProps, headerItem } from '@/components/HeaderActions';
import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Input, InputField } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useConfirmDiscard } from '@/components/useConfirmDiscard';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useColors } from '@/hooks/useColors';
import { useMaskedController } from '@/hooks/useMaskedController';
import { useToaster } from '@/hooks/useToaster';
import { useGoBack } from '@/hooks/useGoBack';
import { clearUserAttributesCache } from '@/services/auth';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import { updateUser } from '@/services/updateUser';
import { segmentClient } from '@/util/analytics';
import { queryClient } from '@/util/queryClient';
import { isPlaceholderEmail } from '@/util/smartLaunch';

const SCREEN_COLUMN = getScreenColumnClasses({ maxWidth: 'max-w-screen-md' });

const contactDetailsSchema = z.object({
  email: z.string().trim().email(),
  notificationEmail: z.string().trim().email().or(z.literal('')),
  mobilePhone: z
    .string()
    .regex(/^\d+$/, 'a mobile number must be 10 digits')
    .trim()
    .length(10, 'a mobile number must be 10 digits'),
});

/**
 * Editing contact details, on its own screen so the bar's Edit morphs into Save as it pushes.
 * Saving or discarding goes back to the read-only screen.
 */
export default function EditContactDetails() {
  const goBack = useGoBack(ROUTES.ACCOUNT_CONTACT_DETAILS);

  const userId = useAtomValue(userIdAtom);

  const userQueryOptions = getCurrentUserV2QueryOptions(userId);

  const userQueryKey = userQueryOptions.queryKey;

  const { data, isLoading } = useQuery(userQueryOptions);

  const notificationEmail = data?.notificationEmail || '';

  /**
   * An account created from an EHR signs in through the EHR and holds a placeholder address
   * that is never mailed. Its primary email stays in the form unchanged, so saving still
   * validates, but it is neither shown nor offered as the notification fallback.
   */
  const hasPlaceholderEmail = isPlaceholderEmail(data?.email);

  const mobilePhoneSliced = data?.mobilePhone?.replace('+1', '') || '';

  const toast = useToaster();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, isDirty, isValid, isSubmitting },
  } = useForm({
    defaultValues: {
      email: data?.email || '',
      notificationEmail,
      mobilePhone: mobilePhoneSliced,
    },
    resolver: zodResolver(contactDetailsSchema),
  });

  const mobileNumber = useMaskedController({
    control,
    type: 'mobilePhone',
    setFormValue: (value) => {
      setValue('mobilePhone', value);

      trigger('mobilePhone');
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        email: data.email || '',
        notificationEmail,
        mobilePhone: mobilePhoneSliced,
      });
    }
  }, [data]);

  useEffect(() => {
    segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
      editType: 'contact',
    });
  }, []);

  const leave = () => {
    Keyboard.dismiss();

    goBack();
  };

  const { confirmCancel, discardModal } = useConfirmDiscard({ isDirty, onDiscard: leave });

  const { mutateAsync: updateUserMutation } = useMutation({
    mutationFn: updateUser,
  });

  const onSubmit = handleSubmit(async (data) => {
    Keyboard.dismiss();

    try {
      await updateUserMutation({
        email: data.email,
        notificationEmail: data.notificationEmail || null,
        mobilePhone: data.mobilePhone ? `+1${data.mobilePhone}` : undefined,
      });

      await queryClient.invalidateQueries({ queryKey: userQueryKey });

      clearUserAttributesCache();

      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_SAVED, {
        editType: 'contact',
        updatedFields: {
          email: {
            old: data?.email,
            new: data.email,
          },
          notificationEmail: {
            old: notificationEmail,
            new: data.notificationEmail,
          },
          mobilePhone: {
            old: mobilePhoneSliced,
            new: data.mobilePhone,
          },
        },
      });

      toast.showSuccess({
        title: 'Contact Details Updated',
      });

      leave();
    } catch (error) {
      console.error('ContactDetails ERROR:', error);

      toast.showError({
        title: 'Unable to update contact details',
        apiError: {
          error,
        },
      });
    }
  });

  const { bottom } = useSafeAreaInsets();

  const isDisabled = !isDirty || !isValid || isSubmitting;

  const { colors } = useColors();

  return (
    <VStack
      className={twJoin('flex-1', SCREEN_COLUMN.frame)}
      style={{
        paddingBottom: bottom,
      }}>
      <Header
        contentScrollsUnderHeader
        statusBarBackgroundColor={colors.background[0]}
        title="Edit Contact Details"
        {...getCancelHeaderProps(confirmCancel)}
        rightItems={[headerItem.save({ onPress: onSubmit, isDisabled, isSaving: isSubmitting })]}
      />

      <VStack className="flex-1">
        {isLoading ? (
          <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0" />
        ) : (
          <ScrollView
            contentInsetAdjustmentBehavior="automatic"
            className="w-full flex-1 gap-4"
            contentContainerClassName={twJoin('gap-4 py-6', SCREEN_COLUMN.content)}
            keyboardDismissMode="on-drag">
            {hasPlaceholderEmail ? null : (
              <>
                <Text className="font-medium text-typography-900">Primary email</Text>

                <Controller
                  name="email"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControl isInvalid={!!errors?.email?.message} className="gap-2">
                      <Input>
                        <InputField
                          style={{ color: colors.typography[900] }}
                          value={value}
                          placeholder="Enter your email"
                          autoCorrect={false}
                          onChangeText={(text) => onChange(text.toLowerCase())}
                          keyboardType="email-address"
                          autoCapitalize="none"
                        />
                      </Input>

                      <Text className="text-xs text-typography-500">
                        Your primary email is used for sign in.
                      </Text>

                      <FormControlError>
                        <FormControlErrorIcon as={AlertCircleIcon} />
                        <FormControlErrorText>
                          {errors?.email?.message as string}
                        </FormControlErrorText>
                      </FormControlError>
                    </FormControl>
                  )}
                />
              </>
            )}

            <VStack>
              <Controller
                name="notificationEmail"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl isInvalid={!!errors?.notificationEmail?.message} className="gap-2">
                    <FormControlLabel>
                      <FormControlLabelText>Notification email</FormControlLabelText>
                    </FormControlLabel>

                    <Input>
                      <InputField
                        style={{ color: colors.typography[900] }}
                        value={value}
                        placeholder={hasPlaceholderEmail ? 'Enter your email' : data?.email || ''}
                        autoCorrect={false}
                        onChangeText={(text) => onChange(text.toLowerCase())}
                        keyboardType="email-address"
                        autoCapitalize="none"
                      />
                    </Input>

                    <Text className="text-xs text-typography-500">
                      Notifications will be sent to this email if provided, otherwise to your
                      primary email.
                    </Text>

                    <FormControlError>
                      <FormControlErrorIcon as={AlertCircleIcon} />

                      <FormControlErrorText>
                        {errors?.notificationEmail?.message as string}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>
                )}
              />
            </VStack>

            <VStack>
              <FormControl isInvalid={!!errors?.mobilePhone?.message} className="gap-2">
                <FormControlLabel>
                  <FormControlLabelText>Mobile number</FormControlLabelText>
                </FormControlLabel>

                <Input>
                  <InputField
                    style={{ color: colors.typography[900] }}
                    textContentType="telephoneNumber"
                    keyboardType="phone-pad"
                    autoComplete="tel"
                    autoCapitalize="none"
                    {...mobileNumber.inputProps}
                  />
                </Input>

                <Text className="text-xs text-typography-500">
                  Notifications will be sent to this number.
                </Text>

                <FormControlError>
                  <FormControlErrorIcon as={AlertCircleIcon} />

                  <FormControlErrorText>
                    {errors?.mobilePhone?.message as string}
                  </FormControlErrorText>
                </FormControlError>
              </FormControl>
            </VStack>
          </ScrollView>
        )}
      </VStack>

      {discardModal}
    </VStack>
  );
}
